import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { EmptyState, PageHeader, SkeletonRows, StatusPill, useDateFmt } from "@/components/common";
import { RenewDialog } from "@/components/RenewDialog";

export default function Renewals() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("due");
  const [renew, setRenew] = useState(null);
  const f = useDateFmt();
  const load = useCallback(() => { api.get("/renewals").then(({ data }) => setD(data)).catch(() => setD({ due: [], in_progress: [], history: [] })); }, []);
  useEffect(load, [load]);
  const tabs = [["due", "Due for renewal", d?.due.length], ["in_progress", "In progress", d?.in_progress.length], ["history", "Completed", d?.history.length]];

  return (
    <div data-testid="renewals-page">
      <PageHeader title="Renewal Center" subtitle="Renew documents and contracts in one click — previous dates and files are always kept." />
      <div className="flex gap-2 mb-5 overflow-x-auto">{tabs.map(([k, l, n]) => <button key={k} className="pm-tab" data-active={tab === k} onClick={() => setTab(k)} data-testid={`renewals-tab-${k}`}>{l}{n !== undefined ? ` (${n})` : ""}</button>)}</div>
      <div className="pm-card p-5 sm:p-6">
        {!d ? <SkeletonRows /> : tab === "history" ? (
          d.history.length ? d.history.map((h) => (
            <Link key={h.id} to={`/reminders/${h.reminder_id}`} className="flex flex-wrap justify-between gap-2 py-4 border-b border-[var(--pm-border)] last:border-0" data-testid={`renewal-history-${h.id}`}>
              <span><span className="font-semibold block">{h.reminder_title}</span><span className="text-xs text-[var(--pm-muted)]">{f(h.previous_date)} → {f(h.new_date)} · renewed {f(h.renewed_at)}</span></span>
              <StatusPill urgency="completed">Renewed</StatusPill>
            </Link>
          )) : <EmptyState title="No renewals completed yet" />
        ) : (d[tab].length ? d[tab].map((r) => {
          const done = r.renewal?.checklist?.filter((c) => c.done).length || 0;
          const total = r.renewal?.checklist?.length || 0;
          return (
            <div key={r.id} className="flex flex-wrap items-center gap-4 py-4 border-b border-[var(--pm-border)] last:border-0" data-testid={`renewal-item-${r.id}`}>
              <Link to={`/reminders/${r.id}`} className="flex-1 min-w-[200px]">
                <span className="font-semibold block">{r.title}</span>
                <span className="text-xs text-[var(--pm-muted)]">{r.subcategory_name} · expires {f(r.event_date)}{tab === "in_progress" ? ` · ${done} of ${total} steps completed` : ""}</span>
                {tab === "in_progress" && total > 0 && <span className="block h-1.5 rounded-full bg-[#ECE9F5] mt-2 max-w-xs overflow-hidden"><span className="block h-full" style={{ width: `${(done / total) * 100}%`, background: "var(--pm-gradient)" }} /></span>}
              </Link>
              <StatusPill urgency={r.urgency}>{r.countdown}</StatusPill>
              <button className="pm-btn pm-btn-sm pm-btn-primary" onClick={() => setRenew(r)} data-testid={`renew-btn-${r.id}`}><RefreshCw className="w-4 h-4" />{tab === "in_progress" ? "Complete" : "Renew"}</button>
            </div>
          );
        }) : <EmptyState icon={RefreshCw} title={tab === "due" ? "Nothing due for renewal in the next 90 days" : "No renewals in progress"} text="Documents and contracts expiring within 90 days appear here." />)}
      </div>
      {renew && <RenewDialog open={!!renew} onClose={() => setRenew(null)} r={renew} onDone={load} />}
    </div>
  );
}
